import CryptoJS from 'crypto-js'

/**
 * Service de chiffrement pour la sécurisation des données sensibles
 * Utilise AES-256 pour le chiffrement des données
 */
class EncryptionService {
  constructor() {
    // Only initialize if we're in browser environment and have a real key
    if (typeof window !== 'undefined' && import.meta.env.VITE_ENCRYPTION_KEY) {
      this.secretKey = import.meta.env.VITE_ENCRYPTION_KEY
    } else {
      this.secretKey = null
      console.warn('⚠️ Encryption service not available - no key configured')
    }
  }

  /**
   * Vérifie si une vraie clé de chiffrement est configurée
   * @returns {boolean} - True si une vraie clé est configurée
   */
  isKeyConfigured() {
    return this.secretKey !== null
  }

  /**
   * Chiffre des données avec AES-256
   * @param {any} data - Données à chiffrer
   * @returns {string|null} - Données chiffrées ou null en cas d'erreur
   */
  encrypt(data) {
    if (!this.isKeyConfigured()) {
      console.warn('⚠️ Encryption not available - storing data unencrypted')
      return typeof data === 'string' ? data : JSON.stringify(data)
    }
    
    try {
      if (!data) return null
      const dataString = typeof data === 'string' ? data : JSON.stringify(data)
      return CryptoJS.AES.encrypt(dataString, this.secretKey).toString()
    } catch (error) {
      console.error('❌ Erreur de chiffrement:', error)
      return null
    }
  }

  /**
   * Déchiffre des données avec AES-256
   * @param {string} encryptedData - Données chiffrées
   * @returns {any|null} - Données déchiffrées ou null en cas d'erreur
   */
  decrypt(encryptedData) {
    if (!this.isKeyConfigured()) {
      console.warn('⚠️ Decryption not available - returning raw data')
      try {
        return JSON.parse(encryptedData)
      } catch {
        return encryptedData
      }
    }
    
    try {
      if (!encryptedData) return null
      const bytes = CryptoJS.AES.decrypt(encryptedData, this.secretKey)
      const decryptedString = bytes.toString(CryptoJS.enc.Utf8)
      
      if (!decryptedString) {
        console.error('❌ Impossible de déchiffrer les données')
        return null
      }
      
      try {
        return JSON.parse(decryptedString)
      } catch {
        return decryptedString
      }
    } catch (error) {
      console.error('❌ Erreur de déchiffrement:', error)
      return null
    }
  }

  /**
   * Hash un mot de passe avec SHA-256
   * @param {string} password - Mot de passe en clair
   * @returns {string} - Hash du mot de passe
   */
  hashPassword(password) {
    try {
      return CryptoJS.SHA256(password).toString()
    } catch (error) {
      console.error('❌ Erreur de hashage du mot de passe:', error)
      return null
    }
  }

  /**
   * Génère une clé de session sécurisée
   * @returns {string} - Clé de session
   */
  generateSessionKey() {
    return CryptoJS.lib.WordArray.random(32).toString()
  }

  /**
   * Génère un ID utilisateur sécurisé
   * @returns {string} - ID utilisateur
   */
  generateUserId() {
    return CryptoJS.lib.WordArray.random(16).toString()
  }

  /**
   * Valide l'intégrité des données chiffrées
   * @param {string} encryptedData - Données chiffrées
   * @returns {boolean} - True si les données sont valides
   */
  isValidEncryptedData(encryptedData) {
    try {
      const decrypted = this.decrypt(encryptedData)
      return decrypted !== null
    } catch {
      return false
    }
  }

  /**
   * Chiffre et stocke des données de manière sécurisée
   * @param {string} key - Clé de stockage
   * @param {any} data - Données à stocker
   */
  secureStore(key, data) {
    try {
      const encrypted = this.encrypt(data)
      if (encrypted) {
        localStorage.setItem(key, encrypted)
        return true
      }
      return false
    } catch (error) {
      console.error('❌ Erreur de stockage sécurisé:', error)
      return false
    }
  }

  /**
   * Récupère et déchiffre des données stockées
   * @param {string} key - Clé de stockage
   * @returns {any|null} - Données déchiffrées ou null
   */
  secureRetrieve(key) {
    try {
      const encrypted = localStorage.getItem(key)
      if (!encrypted) return null
      
      return this.decrypt(encrypted)
    } catch (error) {
      console.error('❌ Erreur de récupération sécurisée:', error)
      return null
    }
  }

  /**
   * Supprime des données stockées de manière sécurisée
   * @param {string} key - Clé de stockage
   */
  secureRemove(key) {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.error('❌ Erreur de suppression sécurisée:', error)
    }
  }
}

// Export d'une instance singleton
export default new EncryptionService()
